import React, { useState } from "react";
import { BarChart3, Clock, AlertCircle, Globe, Loader2 } from "lucide-react";
import AnalyticsSubHeader from "./Analytics/AnalyticsSubHeader";
import AnalyticsStatCards from "./Analytics/AnalyticsStatCards";
import ApiUsageLineChart from "./Analytics/ApiUsageLineChart";
import TokenUsageDonutChart from "./Analytics/TokenUsageDonutChart";
import TopLanguagePairsCard from "./Analytics/TopLanguagePairsCard";
import ServiceRequestsBarChart from "./Analytics/ServiceRequestsBarChart";
import ServicePerformanceTable from "./Analytics/ServicePerformanceTable";
import ActivityLogTable from "./Analytics/ActivityLogTable";

const UsageAnalytics = ({ analytics, loading }) => {
  const safeAnalytics = analytics || {};
  const [activeSubTab, setActiveSubTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");

  if (loading && !analytics) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <Loader2 className="w-10 h-10 text-[#1e6c56] animate-spin" />
        <p className="text-sm font-medium text-gray-500">Loading Usage Analytics...</p>
      </div>
    );
  }

  // Dynamic metrics extraction for current active sub-tab (TTT, TTS, STT, STS, or Overview)
  const getActiveMetrics = () => {
    if (activeSubTab !== "overview" && safeAnalytics.serviceMetrics && safeAnalytics.serviceMetrics[activeSubTab]) {
      const sm = safeAnalytics.serviceMetrics[activeSubTab];
      return {
        totalRequests: sm.totalRequests?.toLocaleString() || "0",
        totalTokens: sm.totalTokens > 1000 ? `${Math.round(sm.totalTokens / 1000)}k` : (sm.totalTokens || "0"),
        successRate: `${sm.successRate || 100}%`,
        avgResponseTime: sm.avgResponseTime > 1000 ? `${(sm.avgResponseTime / 1000).toFixed(2)}s` : `${sm.avgResponseTime || 0}ms`,
        requestsChange: "+0% vs last week",
        tokensChange: "+0% vs last week",
        successChange: "+0% vs last week",
        timeChange: "0ms from last week",
      };
    }

    return {
      totalRequests: safeAnalytics.totalRequests ? safeAnalytics.totalRequests.toLocaleString() : "0",
      totalTokens: safeAnalytics.totalTokens ? (safeAnalytics.totalTokens >= 1000 ? `${Math.round(safeAnalytics.totalTokens / 1000)}k` : `${safeAnalytics.totalTokens}`) : "0",
      successRate: safeAnalytics.successRate !== undefined ? `${safeAnalytics.successRate}%` : "0%",
      avgResponseTime: safeAnalytics.avgResponseTime !== undefined ? `${safeAnalytics.avgResponseTime}ms` : "0ms",
      requestsChange: "+0% vs last week",
      tokensChange: "+0% vs last week",
      successChange: "+0% vs last week",
      timeChange: "0ms from last week",
    };
  };

  const currentMetrics = getActiveMetrics();

  return (
    <div className="space-y-6">
      {/* Sub-Header Tabs: Overview, Text-to-Text, Text-to-Speech, Speech-to-Text, Speech Translations */}
      <AnalyticsSubHeader activeSubTab={activeSubTab} setActiveSubTab={setActiveSubTab} />

      {/* Top 4 Stats Cards */}
      <AnalyticsStatCards metrics={currentMetrics} />

      {/* 7-Day API Usage Spline Line Chart */}
      <ApiUsageLineChart activeSubTab={activeSubTab} dailyServiceUsage={safeAnalytics.dailyServiceUsage} />

      {/* Middle Row Grid */}
      {activeSubTab === "overview" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TokenUsageDonutChart serviceUsage={safeAnalytics.serviceUsage} />
          <TopLanguagePairsCard topLanguagePairs={safeAnalytics.topLanguagePairs} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopLanguagePairsCard topLanguagePairs={safeAnalytics.serviceTopLanguagePairs?.[activeSubTab] || []} />

          {/* Performance Metrics Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-5">Performance Metrics</h3>
            <div className="space-y-5">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><BarChart3 className="w-4 h-4" /></div>
                  <span className="text-sm font-semibold text-gray-900">API Uptime</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{safeAnalytics.uptime || 99.9}%</span>
              </div>
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Clock className="w-4 h-4" /></div>
                  <span className="text-sm font-semibold text-gray-900">Avg Response Time</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{currentMetrics.avgResponseTime}</span>
              </div>
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 text-red-600 rounded-lg"><AlertCircle className="w-4 h-4" /></div>
                  <span className="text-sm font-semibold text-gray-900">Error Rate</span>
                </div>
                <span className="text-sm font-bold text-gray-900">0.2%</span>
              </div>
              <div className="flex items-center justify-between pb-1">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><Globe className="w-4 h-4" /></div>
                  <span className="text-sm font-semibold text-gray-900">BLEU Score</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{safeAnalytics.bleuScore || 0.92}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Successful API Requests per Service (Overview Only) */}
      {activeSubTab === "overview" && (
        <ServiceRequestsBarChart serviceMetrics={safeAnalytics.serviceMetrics} />
      )}

      {/* Per-Service Performance Table (Overview Only) */}
      {activeSubTab === "overview" && (
        <ServicePerformanceTable servicePerformance={safeAnalytics.servicePerformance} />
      )}

      {/* Activity Logs Table */}
      <ActivityLogTable
        activityLogs={safeAnalytics.activityLogs}
        activeSubTab={activeSubTab}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        serviceFilter={serviceFilter}
        setServiceFilter={setServiceFilter}
      />
    </div>
  );
};

export default UsageAnalytics;
